import React, { useState, useCallback, useEffect, useRef, useMemo } from 'react';
import PropTypes from 'prop-types';
import Select from 'react-select';
import { Link } from 'react-router-dom';
import { connect } from 'react-redux';
import AwesomeDebouncePromise from 'awesome-debounce-promise';

import { openArtistModal, openChatOverlay } from '../../Actions/UIActions';
import { getChats, updateChat } from '../../Actions/ChatActions';
import { getBrandId } from '../../Helpers/user_helpers';
import { faSearch } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { GET_CHATS_ERROR } from '../../Actions/ChatActions';
import OpportunityChat from './Elements/OpportunityChat';
import InputActions from '../General/InputActions';

import './OpportunityChats.scss';
import Loader from '../Loader/Loader';

const FILTER_OPTIONS = [
  { value: 'Unread', label: 'Unread' },
  { value: 'All', label: 'All' },
];


const OpportunityChats = props => {
  const { user, chats, opportunity, getChats, updateChat } = props;
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(false);
  const [curFilter, setCurFilter] = useState(FILTER_OPTIONS[1]);

  // state necessary to trigger re-renders, ref necessary to
  // avoid stale references in the getChats callback
  const [curChatSearchQuery, setCurChatSearchQuery] = useState('');
  const lastChatSearchQuery = useRef(curChatSearchQuery);

  const fetchChats = useCallback(() => {
    setIsLoading(true);
    const query = lastChatSearchQuery.current;
    getChats({
      query,
      Brand_id: getBrandId(user),
      opportunityId: opportunity.id
    }).then(res => {
      if (res.type === GET_CHATS_ERROR) {
        window.ALERT.error('Error fetching chats, please reload and try again.');
        setError(true);
        return;
      }

      if (isInitialLoad) {
        setIsInitialLoad(false);
      }
      // If these don't match, then another query has been kicked off
      // since this one started, so we shouldn't stop loading
      if (query === lastChatSearchQuery.current) {
        setIsLoading(false);
      }
    });
  }, [user, opportunity, getChats, isInitialLoad]);

  const performSearch = useCallback(AwesomeDebouncePromise(fetchChats, 400), [fetchChats]);

  // Initial load
  useEffect(() => {
    if (isInitialLoad) {
      fetchChats();
    }
  }, [isInitialLoad, fetchChats]);

  const chatsToShow = useMemo(() => (
    chats.chats.filter(chat => (
      chat.hasNewMessagesForBrand || curFilter.value === 'All'
    ))
  ), [chats, curFilter]);

  return (
    <div className='opportunity-chats-container'>
      <div className='opportunity-chats-header'>
        <div className='opportunity-chats-search-input-container has-actions'>
          <FontAwesomeIcon className='opportunity-chats-search-icon' icon={faSearch} />
          <input
            className='opportunity-chats-search-input'
            placeholder='Search'
            onChange={({ target }) => {
              setCurChatSearchQuery(target.value);
              lastChatSearchQuery.current = target.value;
              performSearch(target.value);
            }}
            value={curChatSearchQuery}
          />
          <InputActions 
            iconClassName='opportunity-chats-search-clear-icon' 
            searchVal={curChatSearchQuery} 
            onCancel={() => {
              setCurChatSearchQuery('')
              lastChatSearchQuery.current = '';
              performSearch('');
            }} 
          />
        </div>
        <Select
          placeholder='View'
          classNamePrefix='opportunity-chats-filter'
          onChange={setCurFilter}
          className='opportunity-chats-filter'
          isOptionDisabled={option => option.disabled}
          isSearchable={false}
          options={FILTER_OPTIONS}
          value={curFilter}
        />
      </div>
      <div className='opportunity-chats-list'>
        {error ? null : (
          isLoading ? (
            <Loader size={100} />
          ) : (
            <>
              {chatsToShow.length ? (
                chatsToShow.map((chat, idx) => (
                  <div key={`${chat.id}-${idx}`} className='opportunity-chat-wrapper'>
                    <OpportunityChat chat={chat} updateChat={updateChat} />
                  </div>
                ))
              ) : (
                <div className='opportunity-chats-no-results'>
                  No Results
                </div>
              )}
              <Link to={'/chat'} className='opportunity-chats-view-all-chats-btn' onClick={() => window.scrollTo(0, 0)}>
                View All Chats
              </Link>
            </>
          )
        )}
      </div>
    </div>
  )
}

OpportunityChats.propTypes = {
  chats: PropTypes.object.isRequired,

  openChatOverlay: PropTypes.func.isRequired,
  updateChat: PropTypes.func.isRequired,
  openArtistModal: PropTypes.func.isRequired,
  getChats: PropTypes.func.isRequired,
};

const mapStateToProps = state => {
  const { user, chats } = state;
  return { user, chats };
};

export default connect(mapStateToProps, {
  getChats,
  updateChat,
  openArtistModal,
  openChatOverlay,
})(OpportunityChats);
